/* ============================================================
   PUBLIC SITE — one continuous scroll. Owns the navigation chrome
   (desktop index rail / mobile top bar + index menu), window-based
   scroll-spy + progress, reveal-on-scroll wrapping, and the footer.
   ============================================================ */
import { useEffect, useState } from 'react'
import { useContent, useSections, useSettings } from '../content/ContentContext.jsx'
import { SECTION_COMPONENTS } from '../sections/index.js'
import { Reveal } from '../components/Reveal.jsx'
import { Icon } from '../components/Icon.jsx'
import { IndexRail } from './IndexRail.jsx'
import { IndexMenu } from './IndexMenu.jsx'

const SOCIAL_LINKS = [
  { key: 'instagram', label: 'Instagram', icon: 'instagram' },
  { key: 'x', label: 'X', icon: 'x' },
  { key: 'facebook', label: 'Facebook', icon: 'facebook' },
]

export function PublicSite() {
  const { content } = useContent()
  const sections = useSections()
  const settings = useSettings()
  const socials = SOCIAL_LINKS.filter((s) => settings.social?.[s.key])
  // Visible sections in order; the rail/menu skip hero + anything hidden.
  const visible = sections.filter((s) => !s.hidden)
  const railSections = visible.filter((s) => s.key !== 'hero')
  const [active, setActive] = useState('philosophy')
  const [progress, setProgress] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    let raf = null
    const measure = () => {
      raf = null
      const max = document.documentElement.scrollHeight - window.innerHeight
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0)
      const mid = window.scrollY + window.innerHeight * 0.34
      let cur = railSections[0]?.key
      railSections.forEach((s) => {
        const el = document.getElementById('sec-' + s.key)
        if (el && el.getBoundingClientRect().top + window.scrollY <= mid) cur = s.key
      })
      setActive(cur)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    measure()
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
    // Re-bind when the visible section list changes (reorder/hide/rename).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections])

  const goTo = (key) => {
    // scrollIntoView honours each section's scroll-margin-top, which
    // clears the fixed mobile top bar (see site.css).
    const el = document.getElementById('sec-' + key)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setMenuOpen(false)
  }

  return (
    <div className="pub">
      <IndexRail active={active} progress={progress} goTo={goTo} sections={railSections} />

      <header className="topbar">
        <div className="wordmark">
          Curated<b>H</b>omes
        </div>
        <button className="index-btn" onClick={() => setMenuOpen(true)}>
          <span className="bars">
            <i></i>
            <i></i>
          </span>{' '}
          INDEX
        </button>
        <div className="progress-top" style={{ width: progress * 100 + '%' }}></div>
      </header>

      <div className="pub-inner">
        {visible.map((s) => {
          const C = SECTION_COMPONENTS[s.key]
          if (!C) return null
          return (
            <Reveal key={s.key}>
              <C />
            </Reveal>
          )
        })}

        <footer className="footer">
          <div className="fmark">CuratedHomes</div>
          <div className="fitalic">{content.footer.tagline}</div>
          {socials.length > 0 && (
            <div className="footer-social">
              {socials.map((s) => (
                <a
                  key={s.key}
                  href={settings.social[s.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                >
                  <Icon name={s.icon} size={18} />
                </a>
              ))}
            </div>
          )}
          <div className="fmicro">{content.footer.micro}</div>
        </footer>
      </div>

      {menuOpen && (
        <IndexMenu
          active={active}
          goTo={goTo}
          close={() => setMenuOpen(false)}
          sections={railSections}
        />
      )}
    </div>
  )
}
